// LocalStorage key for column visibility
const COLUMN_VISIBILITY_STORAGE_KEY = 'tableView_columnVisibility_v1';

// Default column visibility - mandatory columns are always visible
export const DEFAULT_COLUMN_VISIBILITY = {
  // Mandatory columns (always visible, not toggleable)
  select: true,
  name: true,
  status: true,
  actions: true,
  // Optional columns (toggleable)
  priority: true,
  difficulty: false, // Off by default
  dependsOn: true,
  category: true,
  // Additional optional columns (hidden by default)
  inputs: false,
  deliverables: false,
  attachments: false,
  dueDate: false,
  estimatedDurationMinutes: false,
  createdAt: false,
  startedAt: false,
  completedAt: false,
  updatedAt: false,
};

// Columns that cannot be hidden
export const MANDATORY_COLUMNS = ['select', 'name', 'status', 'actions'];

// Column display names for the UI
export const COLUMN_LABELS = {
  select: 'Checkbox',
  name: 'Mission',
  status: 'Status',
  actions: 'Functions',
  priority: 'Priority',
  difficulty: 'Difficulty',
  dependsOn: 'Depends On',
  category: 'Category',
  inputs: 'Inputs',
  deliverables: 'Deliverables',
  attachments: 'Attachments',
  dueDate: 'Due Date',
  estimatedDurationMinutes: 'Est. time',
  createdAt: 'Created Date',
  startedAt: 'Start Date',
  completedAt: 'Completed Date',
  updatedAt: 'Last Updated',
};

/**
 * Read a previously saved column-visibility map, without filling defaults.
 * Missing or unreadable storage returns null so callers can tell "never saved"
 * apart from the built-in defaults.
 * @param {string} campaignId
 * @returns {Object|null}
 */
export const readStoredColumnVisibility = (campaignId) => {
  if (!campaignId) return null;
  try {
    const stored = localStorage.getItem(`${COLUMN_VISIBILITY_STORAGE_KEY}_${campaignId}`);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Get stored column visibility from localStorage
 * @param {string} campaignId - The campaign ID to get column visibility for
 * @returns {Object} The stored column visibility object, merged with defaults
 */
export const getStoredColumnVisibility = (campaignId) => {
  if (!campaignId) return { ...DEFAULT_COLUMN_VISIBILITY };
  try {
    const stored = localStorage.getItem(`${COLUMN_VISIBILITY_STORAGE_KEY}_${campaignId}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      const merged = { ...DEFAULT_COLUMN_VISIBILITY, ...parsed };
      if (merged.estimatedDays !== undefined && merged.estimatedDurationMinutes === undefined) {
        merged.estimatedDurationMinutes = merged.estimatedDays;
        delete merged.estimatedDays;
      }
      return merged;
    }
    return { ...DEFAULT_COLUMN_VISIBILITY };
  } catch {
    return { ...DEFAULT_COLUMN_VISIBILITY };
  }
};

/**
 * Save column visibility to localStorage
 * @param {string} campaignId - The campaign ID to save column visibility for
 * @param {Object} visibility - The column visibility object to save
 */
export const saveColumnVisibility = (campaignId, visibility) => {
  if (!campaignId) return;
  try {
    localStorage.setItem(`${COLUMN_VISIBILITY_STORAGE_KEY}_${campaignId}`, JSON.stringify(visibility));
  } catch (e) {
    console.warn('Failed to save column visibility:', e);
  }
};

/**
 * Check if a column is mandatory (cannot be hidden)
 * @param {string} columnId - The column ID to check
 * @returns {boolean} True if the column is mandatory
 */
export const isMandatoryColumn = (columnId) => {
  return MANDATORY_COLUMNS.includes(columnId);
};
